"use client";

import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { logger } from '@/utils/logger';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Helper function to safely access localStorage
const getInitialTheme = (): Theme => {
  if (typeof window !== 'undefined') {
    try {
      const savedTheme = localStorage.getItem('theme') as Theme | null;
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return savedTheme || (systemPrefersDark ? 'dark' : 'light');
    } catch (e) {
      logger.warn('Failed to access localStorage for theme', { error: e });
      // Fallback to system preference if localStorage is not accessible
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return systemPrefersDark ? 'dark' : 'light';
    }
  }
  // Default theme during server-side rendering
  return 'dark';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use a function to initialize state to prevent hydration mismatch
  const [theme, setTheme] = useState<Theme>(() => 'dark');
  const [mounted, setMounted] = useState(false);

  // Set initial theme after mount (client-side only)
  useEffect(() => {
    setTheme(getInitialTheme());
    setMounted(true);
  }, []);

  // Update document class and localStorage when theme changes
  useEffect(() => {
    if (!mounted) return;
    
    // Update document class
    document.documentElement.setAttribute('data-theme', theme);
    
    // Save to localStorage
    try {
      localStorage.setItem('theme', theme);
    } catch (e) {
      logger.warn('Failed to save theme preference', { error: e });
    }
  }, [theme, mounted]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // Create a stable context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({
    theme: mounted ? theme : 'light',
    toggleTheme
  }), [theme, mounted]);

  // Always render the provider, but with safe defaults until mounted
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 